import EventEmitter from "events";

import type { ApolloClient, NormalizedCacheObject } from "@apollo/client/core";
import { Workspace, Supervisor } from "~/packages/taskrouter";
import { Client, ClientConfigType, ClientEvent } from "~/modules/client";
import { Twilsock, TwilsockEvent } from "~/modules/websocket";
import { Session, SessionEvent } from "~/modules/session";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import {
    TelemetrySdkEvent,
    TelemetrySdkEventGroup,
    TelemetrySdkClient,
    TelemetrySdkEventName,
    TelemetrySdkEventSource
} from "~/modules/telemetrySdkClient";
import { Emitter, proxyEvent } from "~/modules/events";
import { getTelemetrySdkClient } from "~/modules/telemetrySdkClient/telemetrySdkClient";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { TwilsockImpl } from "~/modules/websocket/Twilsock/TwilsockImpl";
import { AccountConfigStore } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigStore/AccountConfigStore";
import type { TaskRouter } from "~/packages/taskrouter/TaskRouter";
import { assertNotEmptyString } from "~/utils/assert";
import { DataClient } from "~/modules/dataClient/DataClient";
import { DataClientImpl } from "~/modules/dataClient/DataClientImpl";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { AnalyticsInstance, EVENTS } from "~/modules/analytics/Analytics";
import { sendTrackingEvent } from "~/modules/actions/ActionUtils";
import { Action } from "~/modules/actions/Action";
import { ConversationClientEvent } from "~/modules/client/Client/ClientEvent";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";

export class ClientImpl implements Client {
    readonly #session: Session;

    readonly #connection: Twilsock;

    readonly #logger: Logger;

    public readonly config: ClientConfigType;

    readonly #ctx: ContextManager;

    readonly #telemetrySdkClient: TelemetrySdkClient;

    readonly #emitter: Emitter;

    readonly #dataClient: DataClient;

    readonly #analytics: AnalyticsInstance;

    readonly #taskRouter: TaskRouter;

    #isDestroyed: boolean;

    constructor(context?: ContextManager) {
        if (context) {
            this.#ctx = context;
        } else {
            this.#ctx = ContextManager.newInstance();
        }
        this.#session = this.#ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        this.#connection = this.#ctx.retrieveInstanceOf(ContextInstance.TwilsockImpl, TwilsockImpl);
        this.config = {
            account: this.#ctx.retrieveInstanceOf(ContextInstance.AccountConfigStore, AccountConfigStore)
        };
        this.#telemetrySdkClient = getTelemetrySdkClient(this.#ctx);
        this.#logger = getLogger(this.#ctx)(LoggerName.Client);
        this.#emitter = new EventEmitter();
        this.#dataClient = this.#ctx.retrieveInstanceOf(ContextInstance.DataClientImpl, DataClientImpl);
        this.#analytics = this.#ctx.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        this.#taskRouter = this.#ctx.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        this.setupProxies();
    }

    setupProxies(): void {
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.TokenAboutToExpire, ClientEvent.TokenAboutToExpire);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.TokenExpired, ClientEvent.TokenExpired);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.TokenUpdated, ClientEvent.TokenUpdated);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.ConnectionError, ClientEvent.ConnectionLost);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.Connected, ClientEvent.ConnectionRestored);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.Disconnected, ClientEvent.Disconnected);
        proxyEvent(this.#session, this.#emitter, SessionEvent.TokenAutoUpdateFailed, ClientEvent.TokenAutoUpdateFailed);
        proxyEvent(
            this.#session,
            this.#emitter,
            SessionEvent.TokenMaxLifetimeReached,
            ClientEvent.TokenMaxLifetimeReached
        );
    }

    async updateToken(token: string): Promise<void> {
        assertNotEmptyString(token, "token");
        const errors: FlexSdkError[] = [];

        if (this.#isDestroyed) {
            throw new FlexSdkError(
                ErrorCode.InvalidState,
                { severity: ErrorSeverity.Error },
                "Client already destroyed"
            );
        }
        this.emitter.emit(ClientEvent.TokenUpdated, token);
        const throwError = (error: Error, service: string) =>
            new FlexSdkError(
                ErrorCode.SDK,
                { severity: ErrorSeverity.Error },
                `Failed to update token on ${service}`,
                error
            );

        if (this.#session) {
            try {
                await this.#session.updateToken(token);
            } catch (error) {
                errors.push(throwError(error, "session"));
            }
        } else {
            this.#logger.warn("Session is undefined, skipping token update for session.");
        }

        if (errors.length) {
            throw new AggregateError(errors);
        }
    }

    #sendDestroyEvent = async (): Promise<void> => {
        try {
            const telemetrySdkClient = this.#telemetrySdkClient;
            const group = telemetrySdkClient.createEventGroup<TelemetrySdkEvent>(TelemetrySdkEventGroup.Default);
            await group.addEvents({
                eventName: TelemetrySdkEventName.ClientDestroyed,
                eventSource: TelemetrySdkEventSource.Client
            });
        } catch (e) {
            this.#logger.error("Failed to send telemetry destroy event", e);
        }
    };

    async destroy(): Promise<void> {
        this.#logger.debug("Client destroy called");
        if (this.#isDestroyed) {
            return;
        }

        this.#isDestroyed = true;
        await this.#sendDestroyEvent();
        await this.#session.destroy();
        this.#emitter.emit(ClientEvent.ClientDestroyed);
        this.#emitter.removeAllListeners();
        this.#dataClient.destroy();
        this.#ctx.destroy();
        this.#logger.debug("Client has been destroyed");
    }

    get roles(): Array<string> {
        this.#taskRouter.init();
        sendTrackingEvent(EVENTS.ClientTokenCompleted, this.#analytics, this.#taskRouter.worker);
        return [...this.#session.roles];
    }

    get token(): string {
        this.#taskRouter.init();
        sendTrackingEvent(EVENTS.ClientTokenCompleted, this.#analytics, this.#taskRouter.worker);
        return this.#session.token;
    }

    get dataClient(): ApolloClient<NormalizedCacheObject> | null {
        return this.#dataClient.client;
    }

    get emitter(): Emitter {
        return this.#emitter;
    }

    get worker(): Supervisor {
        this.#taskRouter.init();
        return this.#taskRouter.worker;
    }

    get workspace(): Workspace {
        this.#taskRouter.init();
        return this.#taskRouter.workspace;
    }

    async getDataClient(): Promise<ApolloClient<NormalizedCacheObject> | null> {
        
        
        return Promise.resolve(this.#dataClient.client);
    }

    async getWorker(): Promise<Supervisor> {
        await this.#taskRouter.init();
        return this.#taskRouter.worker;
    }

    async getWorkspace(): Promise<Workspace> {
        await this.#taskRouter.init();
        return this.#taskRouter.workspace;
    }

    addListener(eventName: ClientEvent | ConversationClientEvent, listener: (...args: unknown[]) => void): this {
        this.#emitter.on(eventName, listener);
        return this;
    }

    removeListener(eventName: ClientEvent, listener: (...args: unknown[]) => void): this {
        this.#emitter.removeListener(eventName, listener);
        return this;
    }

    execute<T>(action: Action<T>): T {
        return action.run(this.#ctx);
    }
}
